import React, { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IconButton, TextField } from '@totalsoft/rocket-ui';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { emptyString } from 'utils/constants';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_FIELD_DECIMAL_PLACES_CONFIG_WRITE } from 'constants/rightsCodes';
import { between, errorMessage, fromModel, required, shape, stopOnFirstFailure } from '@totalsoft/pure-validations';
import i18next from 'i18next';
import { StyledTableBodyCell, StyledTableRow } from 'components/common/Styles';
import FlexBox from 'components/common/FlexBox';
import { FIELD_DECIMAL_PLACES_CONFIG_UPDATED } from './subscriptions';
import { FIELD_DECIMAL_PLACES_CONFIG_QUERY } from './queries';
import { addCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { useToast } from 'hooks/toasts/useToast';
import { useMutation, useSubscription } from 'hooks/apollo';
import { UPDATE_NUMERIC_PRECISION } from './mutations';
import TextTableCell from 'components/common/table/TextTableCell';

const validator = fromModel(model =>
  shape({
    decimalPlaces:
      [required, between(0, model.maxDecimalPlaces) |> errorMessage(i18next.t('NumericPrecisions.Validation'))] |> stopOnFirstFailure
  })
);

const NumericPrecisionsListItemContainer = ({ numericPrecision }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const [validation, validate] = useDirtyFieldValidation(validator);

  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(false);

  const canWrite = usePermission(ADMIN_FIELD_DECIMAL_PLACES_CONFIG_WRITE);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(numericPrecision);

  useEffect(() => {
    resetLens(numericPrecision);
  }, [numericPrecision, resetLens]);

  const updateEditing = useCallback(
    value => () => {
      if (!value) resetLens(numericPrecision);
      setEditing(value);
    },
    [numericPrecision, resetLens]
  );

  // #region Update

  const [updateFieldDecimalPlacesConfig] = useMutation(UPDATE_NUMERIC_PRECISION, {
    onError: () => {
      setPending(false);
    }
  });

  const handleUpdate = useCallback(() => {
    const data = lens |> get;
    if (!validate(data)) return;
    setPending(true);

    updateFieldDecimalPlacesConfig({
      variables: {
        input: {
          fieldType: data.fieldType,
          decimalPlaces: data.decimalPlaces,
          maxDecimalPlaces: data.maxDecimalPlaces
        }
      }
    });
  }, [lens, validate, updateFieldDecimalPlacesConfig]);

  useSubscription(FIELD_DECIMAL_PLACES_CONFIG_UPDATED, {
    skip: !numericPrecision.fieldType,
    variables: { fieldType: numericPrecision.fieldType },
    onData: ({
      data: {
        data: {
          fieldDecimalPlacesConfigUpdated: { fieldDecimalPlacesConfig }
        }
      },
      client
    }) => {
      addCachedDetail(
        client,
        { name: 'fieldDecimalPlacesConfig', value: FIELD_DECIMAL_PLACES_CONFIG_QUERY },
        null,
        x => x.fieldType === fieldDecimalPlacesConfig.fieldType,
        fieldDecimalPlacesConfig,
        ['fieldType']
      );
      setPending(false);
      setEditing(false);
      addToast(t('NumericPrecisions.NumericPrecisionUpdated'), 'success');
    }
  });

  // #endregion

  const disabled = useMemo(() => !canWrite || pending, [canWrite, pending]);
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  return (
    <StyledTableRow>
      <TextTableCell content={numericPrecision.displayName} />
      {editing ? (
        <StyledTableBodyCell>
          {
            <TextField
              label={t('NumericPrecisions.DecimalsPlaces')}
              isNumeric
              fullWidth
              decimalScale={0}
              value={(lens.decimalPlaces |> get) || emptyString}
              onChange={lens.decimalPlaces |> set}
              error={!isValid(validation.decimalPlaces)}
              helperText={getErrors(validation.decimalPlaces)}
              disabled={disabled}
            />
          }
        </StyledTableBodyCell>
      ) : (
        <StyledTableBodyCell>{numericPrecision.decimalPlaces}</StyledTableBodyCell>
      )}
      <StyledTableBodyCell>{numericPrecision.maxDecimalPlaces}</StyledTableBodyCell>
      {editing ? (
        <StyledTableBodyCell>
          <FlexBox gap={1} alignItems='center' justifyContent='end'>
            <IconButton
              size='small'
              type='save'
              variant='text'
              color='primary'
              onClick={handleUpdate}
              tooltip={t('Save')}
              disabled={disabled || !isDirty}
            />
            <IconButton
              size='small'
              type='cancel'
              variant='text'
              color='primary'
              onClick={updateEditing(false)}
              tooltip={t('Cancel')}
              disabled={disabled}
            />
          </FlexBox>
        </StyledTableBodyCell>
      ) : (
        <StyledTableBodyCell>
          <FlexBox gap={1} alignItems='center' justifyContent='end'>
            <IconButton
              size='small'
              type='edit'
              variant='text'
              color='primary'
              onClick={updateEditing(true)}
              tooltip={t('Edit')}
              disabled={disabled}
            />
          </FlexBox>
        </StyledTableBodyCell>
      )}
    </StyledTableRow>
  );
};

NumericPrecisionsListItemContainer.propTypes = {
  numericPrecision: PropTypes.object
};

export default NumericPrecisionsListItemContainer;
